import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { BetaGateProvider, useBetaGate } from "./context/BetaGateContext";
import { initPosthog, identifyUser, resetIdentity } from "./lib/posthog";
import { logActivity } from "./lib/activity";
import { api } from "./lib/api";
import ProtectedRoute from "./components/ProtectedRoute";
import NdaWall from "./components/NdaWall";
import LiveTestersAlert from "./components/LiveTestersAlert";
import ResmoHelper from "./components/ResmoHelper";
import ScreenshotWatermark from "./components/ScreenshotWatermark";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Register from "./pages/Register";
import FeedResume from "./pages/FeedResume";
import FeedJob from "./pages/FeedJob";
import Dashboard from "./pages/Dashboard";
import Compare from "./pages/Compare";
import Resources from "./pages/Resources";
import JobSearch from "./pages/JobSearch";
import EarlyAccess from "./pages/EarlyAccess";
import EarlyAccessAdmin from "./pages/EarlyAccessAdmin";
import Challenge from "./pages/Challenge";
import DecodedBrief from "./pages/DecodedBrief";
import SparkMoment from "./components/SparkMoment";
import Skills from "./pages/Skills";
import History from "./pages/History";
import Applications from "./pages/Applications";
import Onboarding from "./pages/Onboarding";
import PublicShare from "./pages/PublicShare";
import PublicRole from "./pages/PublicRole";
import InterviewQuestions from "./pages/InterviewQuestions";
import SteppingStones from "./pages/SteppingStones";
import Network from "./pages/Network";
import MockInterview from "./pages/MockInterview";
import NewsAdmin from "./pages/NewsAdmin";
import AdminActivity from "./pages/AdminActivity";
import AdminFeedback from "./pages/AdminFeedback";
import BetaInvitesAdmin from "./pages/BetaInvitesAdmin";
import NdaAdmin from "./pages/NdaAdmin";
import ExitSurvey from "./pages/ExitSurvey";
import BetaDocs from "./pages/BetaDocs";
import BetaGate from "./pages/BetaGate";
import BetaWelcome from "./components/BetaWelcome";
import Account from "./pages/Account";
import Billing from "./pages/Billing";
import Welcome from "./pages/Welcome";
import Studio from "./pages/Studio";
import StrategyBrief from "./pages/StrategyBrief";
import InterviewerResearch from "./pages/InterviewerResearch";
import Interviews from "./pages/Interviews";
import InterviewDetail from "./pages/InterviewDetail";
import InterviewDebrief from "./pages/InterviewDebrief";
import RecruiterSignup from "./pages/RecruiterSignup";
import RecruiterDashboard from "./pages/RecruiterDashboard";
import RecruiterMatchmaker from "./pages/RecruiterMatchmaker";
import EmployerSignup from "./pages/EmployerSignup";
import EmployerLogin from "./pages/EmployerLogin";
import EmployerPending from "./pages/EmployerPending";
import Discover from "./pages/Discover";
import CandidatesStore from "./pages/CandidatesStore";
import EmployerShortlist from "./pages/EmployerShortlist";
import PublicResume from "./pages/PublicResume";
import EmployersAdmin from "./pages/EmployersAdmin";
import EmployerRequests from "./pages/EmployerRequests";
import EmployerRequestDetail from "./pages/EmployerRequestDetail";
import CandidateRequests from "./pages/CandidateRequests";
import EmployerProtected from "./components/employer/EmployerProtected";
import IntroRespond from "./pages/IntroRespond";
import PublicStrategyBrief from "./pages/PublicStrategyBrief";
import PublicPlaybook from "./pages/PublicPlaybook";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import { ActingAsProvider } from "./context/ActingAsContext";
import ActingAsBanner from "./components/ActingAsBanner";
import ResumeBuilder from "./pages/ResumeBuilder";
import BulkResult from "./pages/BulkResult";
import PlaybooksList from "./pages/PlaybooksList";
import PlaybookDetail from "./pages/PlaybookDetail";
import BetaGuide from "./pages/BetaGuide";
import OnboardingTour from "./components/OnboardingTour";
import RoutedTutorials from "./components/RoutedTutorials";
import ProMoNudge from "./components/ProMoNudge";
import Watermark from "./components/Watermark";
import DisclaimerFooter from "./components/DisclaimerFooter";
import { BillingProvider } from "./context/BillingContext";
import PaywallModal from "./components/PaywallModal";
import FeedbackButton from "./components/FeedbackButton";
import BackToDashboard from "./components/BackToDashboard";
import PrivateModeGate from "./components/PrivateModeGate";

/**
 * Gates the whole app behind the beta passphrase. Public share pages bypass
 * the gate (their tokens are themselves access control).
 */
function ActivityTracker() {
  // Init PostHog on first mount + emit page_view on every route change.
  // Identifies the user when auth resolves; resets on logout.
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => { initPosthog(); }, []);
  useEffect(() => {
    if (user?.id) identifyUser(user);
    else resetIdentity();
  }, [user?.id, user?.email, user?.name]);
  useEffect(() => {
    logActivity("page_view", {}, location.pathname + (location.search || ""));
  }, [location.pathname, location.search]);
  // Presence heartbeat — every 2 min while a tab is open, so admins can see
  // who's live in the app before pushing changes.
  useEffect(() => {
    if (!user?.id) return;
    const beat = () => api.post("/activity/heartbeat").catch(() => {});
    beat();
    const t = setInterval(beat, 120000);
    return () => clearInterval(t);
  }, [user?.id]);
  return null;
}

// ────────────────────────────────────────────────────────────────────
// Beta-gate allow-list — routes that MUST be reachable without a beta
// invite code. Everything else falls through to <BetaGate>.
//
// This used to be a rats-nest of `path.startsWith(...) || path === ...`
// checks inside `GatedApp`. It broke every time we shipped a new public
// page (recruiter signup, employer portal, share pages, welcome tour).
// Now each entry is either:
//   { prefix: "/employer/" }   — matches anything under the prefix
//   { exact:  "/login" }       — exact-path match
// Adding a new public page = one line here, no risk of typos elsewhere.
// ────────────────────────────────────────────────────────────────────
const PUBLIC_ROUTES = [
  // Public share pages (résumés, roles, intros, strategy briefs, playbooks)
  { prefix: "/share/" },
  { prefix: "/role/" },
  { prefix: "/intro/" },
  { prefix: "/strategy-brief/share/" },
  { prefix: "/playbook/share/" },
  // Legal
  { exact:  "/privacy" },
  { exact:  "/terms" },
  // Marketing / welcome / gate itself
  { exact:  "/" },
  { exact:  "/welcome" },
  { exact:  "/login" },
  { exact:  "/forgot-password" },
  { exact:  "/reset-password" },
  { exact:  "/gate" },
  { exact:  "/early-access" },
  { exact:  "/challenge" },
  { prefix: "/decoded/" },
  { prefix: "/beta-guide" },
  // Resmo Pro — recruiter portal (has its own recruiter-only gate server-side)
  { prefix: "/recruiter" },
  // Talent Directory — employers use a separate `employer_token` JWT and
  // admin-verification flow. Must be reachable without a candidate invite.
  { prefix: "/employer/" },
  { exact:  "/discover" },
  { prefix: "/discover/" },
  { prefix: "/r/" },
];

function isPublicPath(path) {
  return PUBLIC_ROUTES.some((r) =>
    (r.exact && r.exact === path) ||
    (r.prefix && path.startsWith(r.prefix)),
  );
}

function GatedApp({ children }) {
  const { loading, unlocked, celebrating } = useBetaGate();
  const { user } = useAuth();
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (isPublicPath(path)) return children;
  if (loading || (user === null && !unlocked)) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: "#0F1B2D", color: "#FFC145" }}>
        <div className="font-mono-rm text-xs uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  // Signed-in admins are never gated — the beta invite wall is for testers.
  if (user && user.is_admin) return children;
  if (celebrating) return <BetaWelcome />;
  if (!unlocked) return <BetaGate />;
  return children;
}

// NDA front wall — once a candidate with `nda_required` is signed in, they
// can't browse ANY page (public landing/welcome included) until they sign.
// Only the legal pages stay reachable so they can read what they're agreeing to.
function NdaGate({ children }) {
  const { user, refresh } = useAuth();
  const { pathname } = useLocation();
  const exempt = pathname === "/privacy" || pathname === "/terms";
  if (user && user.nda_required && !user.nda_signed && !exempt) {
    return <NdaWall onSigned={refresh} />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BetaGateProvider>
            <ThemeProvider>
              <BillingProvider>
                <ActingAsProvider>
                  <ScreenshotWatermark />
                  <PrivateModeGate>
                  <GatedApp>
                    <NdaGate>
                    <ActivityTracker />
                    <SparkMoment />
                    <ActingAsBanner />
                    <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route path="/beta-guide" element={<BetaGuide />} />
                  <Route path="/beta-guide/" element={<BetaGuide />} />
                  <Route path="/gate" element={<BetaGate />} />
                  {/* Alias used by the invite email links — same component as /gate. */}
                  <Route path="/beta" element={<BetaGate />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/early-access" element={<EarlyAccess />} />
                  <Route path="/challenge" element={<Challenge />} />
                  <Route path="/decoded/:token" element={<DecodedBrief />} />
                  <Route path="/admin/early-access" element={<ProtectedRoute><EarlyAccessAdmin /></ProtectedRoute>} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/feed-resume" element={<ProtectedRoute><FeedResume /></ProtectedRoute>} />
                  <Route path="/resume-builder" element={<ProtectedRoute><ResumeBuilder /></ProtectedRoute>} />
                  <Route path="/feed-job" element={<ProtectedRoute><FeedJob /></ProtectedRoute>} />
                  <Route path="/bulk/:id" element={<ProtectedRoute><BulkResult /></ProtectedRoute>} />
                  <Route path="/dashboard/:id" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                  <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
                  <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                  <Route path="/compare/:idA/:idB" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
                  <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
                  <Route path="/resources/interview/new" element={<ProtectedRoute><InterviewQuestions mode="new" /></ProtectedRoute>} />
                  <Route path="/interview-questions/:id" element={<ProtectedRoute><InterviewQuestions mode="result" /></ProtectedRoute>} />
                  <Route path="/stepping-stones" element={<ProtectedRoute><SteppingStones mode="list" /></ProtectedRoute>} />
                  <Route path="/stepping-stones/new" element={<ProtectedRoute><SteppingStones mode="new" /></ProtectedRoute>} />
                  <Route path="/stepping-stones/:id" element={<ProtectedRoute><SteppingStones mode="detail" /></ProtectedRoute>} />
                  <Route path="/network" element={<ProtectedRoute><Network /></ProtectedRoute>} />
                  <Route path="/mock-interview" element={<ProtectedRoute><MockInterview /></ProtectedRoute>} />
                  <Route path="/mock-interview/:id" element={<ProtectedRoute><MockInterview mode="runner" /></ProtectedRoute>} />
                  <Route path="/playbooks" element={<ProtectedRoute><PlaybooksList /></ProtectedRoute>} />
                  <Route path="/playbook/:id" element={<ProtectedRoute><PlaybookDetail /></ProtectedRoute>} />
                  <Route path="/admin/news" element={<ProtectedRoute><NewsAdmin /></ProtectedRoute>} />
                  <Route path="/admin/activity" element={<ProtectedRoute><AdminActivity /></ProtectedRoute>} />
                  <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedback /></ProtectedRoute>} />
                  <Route path="/admin/beta" element={<ProtectedRoute><BetaInvitesAdmin /></ProtectedRoute>} />
                  <Route path="/admin/nda" element={<ProtectedRoute><NdaAdmin /></ProtectedRoute>} />
                  <Route path="/exit-survey" element={<ProtectedRoute><ExitSurvey /></ProtectedRoute>} />
                  {/* Alias — easier to remember/type than /admin/beta */}
                  <Route path="/admin/beta-invites" element={<ProtectedRoute><BetaInvitesAdmin /></ProtectedRoute>} />
                  <Route path="/admin/beta/playbook" element={<ProtectedRoute><BetaDocs kind="admin-playbook" /></ProtectedRoute>} />
                  <Route path="/admin/beta/tester-guide" element={<ProtectedRoute><BetaDocs kind="tester-guide" /></ProtectedRoute>} />
                  <Route path="/admin/beta/welcome" element={<ProtectedRoute><BetaDocs kind="beta-welcome" /></ProtectedRoute>} />
                  <Route path="/job-search" element={<ProtectedRoute><JobSearch /></ProtectedRoute>} />
                  <Route path="/skills" element={<ProtectedRoute><Skills mode="list" /></ProtectedRoute>} />
                  <Route path="/skills/new" element={<ProtectedRoute><Skills mode="new" /></ProtectedRoute>} />
                  <Route path="/skills/role/new" element={<ProtectedRoute><Skills mode="role_new" /></ProtectedRoute>} />
                  <Route path="/skills/role/:id" element={<ProtectedRoute><Skills mode="role_result" /></ProtectedRoute>} />
                  <Route path="/skills/:id" element={<ProtectedRoute><Skills mode="result" /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                  <Route path="/studio" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
                  <Route path="/strategy-brief" element={<ProtectedRoute><StrategyBrief /></ProtectedRoute>} />
                  <Route path="/strategy-brief/:id" element={<ProtectedRoute><StrategyBrief /></ProtectedRoute>} />
                  <Route path="/interviewer-research" element={<ProtectedRoute><InterviewerResearch /></ProtectedRoute>} />
                  <Route path="/interviews" element={<ProtectedRoute><Interviews /></ProtectedRoute>} />
                  <Route path="/interviews/:id/debrief" element={<ProtectedRoute><InterviewDebrief /></ProtectedRoute>} />
                  <Route path="/interviews/:id" element={<ProtectedRoute><InterviewDetail /></ProtectedRoute>} />
                  <Route path="/recruiter/signup" element={<ProtectedRoute><RecruiterSignup /></ProtectedRoute>} />
                  <Route path="/recruiter/matchmaker" element={<ProtectedRoute><RecruiterMatchmaker /></ProtectedRoute>} />
                  <Route path="/recruiter" element={<ProtectedRoute><RecruiterDashboard /></ProtectedRoute>} />
                  <Route path="/intro/respond" element={<IntroRespond />} />
                  <Route path="/strategy-brief/share/:token" element={<PublicStrategyBrief />} />
                  <Route path="/playbook/share/:token" element={<PublicPlaybook />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/share/:token" element={<PublicShare />} />
                  <Route path="/role/:token" element={<PublicRole />} />
                  {/* Employer / Talent-Directory routes (Phase 1). Signup +
                      login are public. Discover + PublicResume gated behind
                      EmployerProtected (verified employers only). */}
                  <Route path="/employer/signup" element={<EmployerSignup />} />
                  <Route path="/employer/login" element={<EmployerLogin />} />
                  <Route path="/employer/pending" element={<EmployerPending />} />
                  <Route path="/discover" element={
                    <EmployerProtected pendingElement={<EmployerPending />}>
                      <Discover />
                    </EmployerProtected>
                  } />
                  {/* Candidates Store — richer employer storefront. Allow
                      pending employers in so they see anonymized preview +
                      a nudge to verify. Verified employers get full data. */}
                  <Route path="/employer/store" element={
                    <EmployerProtected allowPending pendingElement={<EmployerPending />}>
                      <CandidatesStore />
                    </EmployerProtected>
                  } />
                  {/* Shortlist — verified employers only (403 for pending) */}
                  <Route path="/employer/shortlist" element={
                    <EmployerProtected pendingElement={<EmployerPending />}>
                      <EmployerShortlist />
                    </EmployerProtected>
                  } />
                  <Route path="/r/:public_id" element={
                    <EmployerProtected pendingElement={<EmployerPending />}>
                      <PublicResume />
                    </EmployerProtected>
                  } />
                  {/* Employer outbox — verified employers see their outgoing requests */}
                  <Route path="/employer/requests" element={
                    <EmployerProtected pendingElement={<EmployerPending />}>
                      <EmployerRequests />
                    </EmployerProtected>
                  } />
                  <Route path="/employer/requests/:id" element={
                    <EmployerProtected pendingElement={<EmployerPending />}>
                      <EmployerRequestDetail />
                    </EmployerProtected>
                  } />
                  {/* Candidate inbox — incoming outreach with accept/decline */}
                  <Route path="/requests" element={
                    <ProtectedRoute><CandidateRequests /></ProtectedRoute>
                  } />
                  <Route path="/admin/employers" element={
                    <ProtectedRoute><EmployersAdmin /></ProtectedRoute>
                  } />
                </Routes>
                <OnboardingTour />
                <RoutedTutorials />
                <ProMoNudge />
                <PaywallModal />
                <FeedbackButton />
                <BackToDashboard />
                <DisclaimerFooter />
                <Watermark />
                <LiveTestersAlert />
                <ResmoHelper />
                    </NdaGate>
                  </GatedApp>
                  </PrivateModeGate>
                </ActingAsProvider>
              </BillingProvider>
            </ThemeProvider>
          </BetaGateProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
